import { useEffect, useState, type ReactNode } from 'react';

/**
 * Um bloco de conteúdo que abre e fecha.
 *
 * A ficha do cliente juntou identificação, contatos, contratos, casos e
 * anotações. Tudo aberto, a página fica longa demais para se achar nela.
 *
 * **O estado é lembrado por seção**, no navegador: quem trabalha em contratos
 * fecha casos uma vez e não precisa fechar de novo amanhã. Fica no aparelho e
 * não no banco — é preferência de tela, não dado da empresa.
 */
function chave(id: string): string {
  return `kairos-secao-${id}`;
}

export function Secao({
  id,
  titulo,
  subtitulo,
  contador,
  acao,
  antes,
  resumo,
  abrirEm,
  desenho,
  children,
}: {
  id: string;
  /**
   * ⭐ `folha`: o desenho do sistema visual 2026 (14/09/2026) — título em
   * Cormorant com a contagem num selo, e o botão de recolher à direita. Cada
   * tela passa a usá-lo na sua parte; sem ele, a seção fica como era.
   */
  desenho?: 'folha';
  titulo: string;
  subtitulo?: string;
  contador?: number;
  /**
   * Um contador que, ao mudar, **abre a seção**.
   *
   * ⚠️ Existe porque os números da ficha filtram esta lista. Sem isto, clicar
   * em "Concluídos" com a seção fechada não mostraria nada — a tela pareceria
   * quebrada quando na verdade obedeceu.
   */
  abrirEm?: number;
  /**
   * Algo à ESQUERDA do título, fora do gatilho — uma caixa de seleção, por
   * exemplo.
   *
   * ⚠️ **Fora do gatilho porque controle dentro de botão é HTML inválido**, e
   * porque clicar na caixa não pode abrir a seção nem abrir a seção pode
   * marcar a caixa. São duas decisões diferentes, e cada uma tem o seu alvo.
   */
  antes?: ReactNode;
  /** Botão de ação principal — some quando a seção está fechada. */
  acao?: ReactNode;
  /**
   * Um resumo que **continua visível com a seção fechada**.
   *
   * ⚠️ **É o oposto de `acao`, e é essa a razão de existir.** `acao` some ao
   * fechar, porque botão de seção fechada age sobre o que não se vê. Resumo é o
   * contrário: fechar a seção é justamente quando o número importa — é o que
   * permite varrer dez investigados sem abrir nenhum.
   *
   * ⚠️ **Não use para dinheiro em texto cru.** Passe o componente `Valor`, ou o
   * sigilo do cabeçalho é contornado por uma string já formatada.
   */
  resumo?: ReactNode;
  children: ReactNode;
}) {
  const [aberta, setAberta] = useState<boolean>(
    () => localStorage.getItem(chave(id)) !== 'fechada',
  );

  useEffect(() => {
    if (abrirEm) setAberta(true);
  }, [abrirEm]);

  function alternar() {
    const nova = !aberta;
    setAberta(nova);
    localStorage.setItem(chave(id), nova ? 'aberta' : 'fechada');
  }

  if (desenho === 'folha') {
    return (
      <section
        id={`bloco-${id}`}
        className={'bloco s-folha secao-folha' + (aberta ? '' : ' bloco-fechado')}
      >
        <div className="bloco-topo">
          {antes}
          <button
            type="button"
            className="secao-gatilho"
            onClick={alternar}
            aria-expanded={aberta}
            aria-controls={`secao-${id}`}
          >
            <span>
              <span className="titulo-secao">
                {titulo}
                {contador !== undefined && contador > 0 && (
                  <span className="s-chip contador">{contador}</span>
                )}
              </span>
              {subtitulo && <span className="subtitulo secao-subtitulo">{subtitulo}</span>}
            </span>
          </button>

          {resumo}
          {aberta && acao}
          {/* O mesmo gesto do título, num alvo que se acha de olho: a seta
              pequena à esquerda do título sumia no desenho novo. */}
          <button
            type="button"
            className="secao-recolher"
            onClick={alternar}
            aria-expanded={aberta}
            aria-controls={`secao-${id}`}
            aria-label={aberta ? `Recolher ${titulo}` : `Abrir ${titulo}`}
            title={aberta ? 'Recolher' : 'Abrir'}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={aberta ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
            </svg>
          </button>
        </div>

        <div id={`secao-${id}`} className="secao-corpo" hidden={!aberta}>
          {children}
        </div>
      </section>
    );
  }

  return (
    <section id={`bloco-${id}`} className={'bloco' + (aberta ? '' : ' bloco-fechado')}>
      <div className="bloco-topo">
        {antes}
        {/* O título inteiro é o alvo do clique, não só a seta: alvo pequeno
            em tela de toque é alvo errado. */}
        <button
          type="button"
          className="secao-gatilho"
          onClick={alternar}
          aria-expanded={aberta}
          aria-controls={`secao-${id}`}
        >
          <span className="secao-seta" aria-hidden="true">
            {aberta ? '▾' : '▸'}
          </span>
          <span>
            <span className="titulo-secao">
              {titulo}
              {contador !== undefined && contador > 0 && (
                <span className="contador"> {contador}</span>
              )}
            </span>
            {subtitulo && <span className="subtitulo secao-subtitulo">{subtitulo}</span>}
          </span>
        </button>

        {resumo}
        {aberta && acao}
      </div>

      <div id={`secao-${id}`} hidden={!aberta}>
        {children}
      </div>
    </section>
  );
}
